import { useQuery } from '@tanstack/react-query'

const EDGE_API = import.meta.env.VITE_EDGE_API_URL ?? ''

export interface ThemePresetSummary {
  id: string
  name: string
  description: string | null
  thumbnail_url: string | null
  sort_order: number
  // Grouping metadata (migration 043) — may be absent from older edge API responses
  palette_group?: string | null
  mode?: 'dark' | 'light' | 'any'
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_surface: string
  color_text_primary: string
  color_text_muted: string
  color_border: string
  font_family_heading: string
  font_family_body: string
  font_family_decorative?: string
  border_radius_base: string
}

export interface BlockConfig {
  id: string
  component: string
  enabled: boolean
  order: number
  settings: Record<string, unknown>
}

export interface ThemePresetFull extends ThemePresetSummary {
  custom_css: string | null
  homepage_sections: BlockConfig[]
  page_about: BlockConfig[]
  page_events: BlockConfig[]
  page_members: BlockConfig[]
  page_music: BlockConfig[]
  page_shop: BlockConfig[]
}

/**
 * Fetches active theme presets (summary only) for the tenant picker grid.
 * Public endpoint — no auth needed.
 */
export function useThemePresets() {
  return useQuery<ThemePresetSummary[]>({
    queryKey: ['theme-presets'],
    queryFn: async () => {
      const res = await fetch(`${EDGE_API}/theme-presets`)
      if (!res.ok) throw new Error('Gagal memuat tema preset')
      return res.json()
    },
    staleTime: 60_000,
  })
}

/**
 * Fetches a single full preset (including page JSONB).
 * Called only when the tenant is about to apply — not cached aggressively.
 */
export function useThemePresetFull(id: string | null) {
  return useQuery<ThemePresetFull>({
    queryKey: ['theme-preset-full', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await fetch(`${EDGE_API}/theme-presets/${id}`)
      if (!res.ok) throw new Error('Gagal memuat detail tema')
      return res.json()
    },
    staleTime: 0,
  })
}
